/* eslint-disable react/jsx-handler-names */

"use client";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useDispatch, useSelector } from "react-redux";
import {
  clearPersonlizedImageStore,
  setUserPersonalization,
  uploadImageForPersonalization,
} from "../../../../store/slices/personalizationSlice";
import PropTypes from "prop-types";
import {
  generateFilePath,
  handlePersonalizationDetailsOnRefresh,
  handleSetUserPersonalizations,
  isValidFileSize,
  validateExtension,
} from "../../../../utils/pdp/personalization";
import { removeLocalStorageItem } from "../../../../utils/storage/localStorageUtil";
import { constants } from "../../../../constants/pageConstants/pdpConstants";
import useAnalytics from "../../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import { COUNTRIES } from "../../../../constants/common/common";

const FileInput = dynamic(() => import("../../../atoms/fileInput"));
const Toast = dynamic(() => import("../../../atoms/toast"));

/**
 * SingleImagePersonalization component manages image uploads for personalization.
 * It handles file validation (type and size), triggers the upload action,
 * and displays upload progress via a toast notification.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.item - Contains the allowed file types, size limits, and field information for the image upload.
 * @param {number} [props.indexNumber=0] - The index number of the current image upload (optional).
 * @param {function} props.setPreviewImage - Function to update the preview image upon successful upload.
 * @param {function} props.productId - product id associated with the product.
 * @param {function} props.setImageSizeError - Function to handle errors related to invalid file size.
 *
 * @returns {JSX.Element} The image personalization component.
 */
const SingleImagePersonalization = ({
  item,
  indexNumber = 0,
  previewImage,
  setPreviewImage,
  setImageSizeError,
  productId,
  cleverTapData,
}) => {
  const { allowedMediaType, minSize, minUnit, maxSize, maxUnit, contentType, fieldName, required, label } = item;
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [isToastVisible, setIsToastVisible] = useState(false);
  const uploadProgressStatus = useSelector((state) => state.personalization?.uploadProgressStatus);
  const uploadFinalStatus = useSelector((state) => state.personalization?.uploadFinalStatus);
  const selectedPincode = useSelector((state) => state.locationLock.selectedDomesticLocation?.pincode);
  const selectedIntLocation = useSelector((state) => state.locationLock.selectedIntLocation);
  const userPersonalization = useSelector((state) => state.personalization.userPersonalization);
  const presignedUrl = useSelector((state) => state.personalization.presignedUrl);
  const catalogId = useSelector((state) => state?.appConfig?.catalogId);

  useEffect(() => {
    if (presignedUrl) {
      if (contentType === constants.IMAGE || contentType === constants.AUDIO) {
        const url = new URL(presignedUrl);
        const imagePath = url.origin + url.pathname;
        const personalizationValue = {
          fieldName,
          contentType,
          label,
          index: indexNumber,
          value: [imagePath],
        };
        handleSetUserPersonalizations(productId, personalizationValue, userPersonalization, dispatch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignedUrl]);

  useEffect(() => {
    if (uploadFinalStatus && presignedUrl) {
      if (contentType === constants.IMAGE) {
        const url = new URL(presignedUrl);
        const imagePath = url.origin + url.pathname;
        setPreviewImage(imagePath);
        trackEvent(
          cleverTapCommonEvents.uploadPhotoDone,
          {
            ...cleverTapData,
            photo_count: 1,
            is_photo_uploaded: true,
          },
          ["clevertap"],
        );
      } else if (contentType === constants.AUDIO) {
        setPreviewImage(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignedUrl, uploadFinalStatus]);

  useEffect(() => {
    // Fetch data from local storage on component load
    const personalizationFromLocalStore = handlePersonalizationDetailsOnRefresh(productId);
    if (personalizationFromLocalStore && Array.isArray(personalizationFromLocalStore)) {
      dispatch(setUserPersonalization(personalizationFromLocalStore));
      const matchingEntry = personalizationFromLocalStore?.find((entry) => entry.fieldName === fieldName);
      if (matchingEntry && matchingEntry.value?.[0]) {
        const url = new URL(matchingEntry.value?.[0]);
        const imagePath = url.origin + url.pathname;
        setPreviewImage(imagePath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, productId]);

  /**
   * Handles file input change event for uploading personalized images.
   * Validates the file type and size before dispatching the upload action.
   *
   * @param {Object} eventObject - The event object from the input change event.
   * @param {HTMLInputElement} eventObject.target - The target element containing the file input.
   * @returns {void}
   */
  const onFileChange = async ({ target }) => {
    setImageSizeError(false);

    const file = target.files[0];

    if (typeof file === "undefined") {
      return;
    }

    let fileType = file?.type;
    if (fileType === "audio/mpeg") {
      fileType = "audio/mp3";
    } else if (fileType === "image/jpeg") {
      fileType = "image/jpg";
    }

    if (!validateExtension(allowedMediaType.toLowerCase(), fileType.toLowerCase())) {
      dispatch(clearPersonlizedImageStore());
      removeLocalStorageItem(productId);
      return;
    }

    if (isValidFileSize(minSize, minUnit, maxSize, maxUnit, file.size)) {
      if (uploadFinalStatus) {
        dispatch(clearPersonlizedImageStore());
      }
      const fileName = generateFilePath(file.name);
      dispatch(uploadImageForPersonalization({ fileName, file }));
      setIsToastVisible(true);
    } else {
      removeLocalStorageItem(productId);
      setImageSizeError(true);
      setPreviewImage(null);
      dispatch(clearPersonlizedImageStore());
    }
  };

  /**
   * Determines the appropriate constant based on the provided parameters.
   * @returns {string} - The corresponding constant.
   */
  const getUploadLabel = () => {
    if (previewImage) {
      return contentType === constants.AUDIO ? constants.CHANGE_AUDIO : constants.CHANGE_PHOTO;
    }
    return contentType === constants.AUDIO ? constants.UPLOAD_AUDIO : constants.UPLOAD_PHOTO;
  };

  return (
    <>
      <FileInput
        accept={allowedMediaType}
        id={`${fieldName}${indexNumber}`}
        named={`${fieldName}${indexNumber}`}
        isRequired={required === "Y"}
        label={getUploadLabel()}
        onChangeHandler={onFileChange}
        disabled={COUNTRIES.INDIA === catalogId ? !selectedPincode : !selectedIntLocation?.pincode}
        variant="buttonType"
      />
      {isToastVisible && (
        <div className="fixed bottom-[120px] left-1/2 z-20 flex w-11/12 max-w-md -translate-x-1/2 transform justify-center">
          <Toast
            variant={uploadFinalStatus ? constants.SUCCESS : constants.INFO}
            isToast={isToastVisible}
            header={uploadFinalStatus ? constants.SUCCESSFULLY_UPLOADED : constants.UPLOADING}
            subHeader={uploadFinalStatus ? "" : constants.WINDOW_REFRESH_MESSAGE}
            duration={3000}
            onToastChange={() => {
              uploadFinalStatus && setIsToastVisible(false);
            }}
            fill={uploadProgressStatus?.percentComplete}
            strokeWidth="10"
          />
        </div>
      )}
    </>
  );
};

SingleImagePersonalization.propTypes = {
  item: PropTypes.shape({
    allowedMediaType: PropTypes.string.isRequired,
    minSize: PropTypes.string.isRequired,
    minUnit: PropTypes.string.isRequired,
    maxSize: PropTypes.string.isRequired,
    maxUnit: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.string.isRequired,
  }),
  indexNumber: PropTypes.number,
  setPreviewImage: PropTypes.func,
  setImageSizeError: PropTypes.func,
  productId: PropTypes.string.isRequired,
  previewImage: PropTypes.string,
  cleverTapData: PropTypes.object,
};

export default SingleImagePersonalization;
